<script setup lang="ts">
import { Filter, SearchFilters, useSearch, useFilters } from '@ilteducation/content-search';
import { SortKey, SearchPanel } from '@ilteducation/content-search';
import { computed, ComputedRef, onMounted, ref, watch } from 'vue';
import { AvailabilityRealmsFilter, RoleFilter } from '@ilteducation/content-search/src/content-filters';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useCategories } from '@ilteducation/content-ref-data';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import useMaterialFilterFields from '@/composables/use-materials-filter-fields';
import useShortcutMaterialCategories from '@/composables/use-shortcut-material-categories';
import { MATERIAL_CATEGORY_ALL_ID } from '@/composables/use-all-materials-category';
import { useDisplay } from 'vuetify';
import { useLocale } from '@/plugins/translation';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { ContentWithDuplicates, groupContentByOriginalTitle } from '@ilteducation/content';
import { useDialogFocus } from '@ilteducation/components';
import ContentWithFiltersLayout from '@/layouts/ContentWithFiltersLayout.vue';
import MaterialResults from '@/components/material/MaterialResults.vue';

const userContext = useUserContext();
const { roles, realm } = storeToRefs(userContext);
const subscriptions = useSubscriptions();
const router = useRouter();

const { smAndDown } = useDisplay();
const { t, l } = useLocale();

const searchStore = useSearch<MaterialV1>('Material');
const { next } = searchStore;
const { result, isLoading, hasMore } = storeToRefs(searchStore);

const sortFields = ['lastAdded', 'titleDesc', 'titleAsc'] as SortKey[];
const { selectedFilters, filtersToQuery, removeAllFilters } = useFilters();
const { load: loadCategories } = useCategories();
const { materialCategoryItems } = useShortcutMaterialCategories();
const limit = 100;
const materialFilterFields = useMaterialFilterFields();
const filterFields = computed(() => [...materialFilterFields.value]);
const isFiltersVisible = ref<boolean>(!smAndDown.value);
const { rememberFocus } = useDialogFocus([isFiltersVisible]);
const searchPanelRef = ref<InstanceType<typeof SearchPanel>>();

const filters: ComputedRef<Filter> = computed(() => ({
  ...RoleFilter(roles.value.map((role) => role.value).toString()),
  ...AvailabilityRealmsFilter(realm.value),
}));

const deduplicatedMaterials = ref<MaterialV1[]>([]);

const sortBySwedishFirst = (a: ContentWithDuplicates, b: ContentWithDuplicates) => {
  if ((a as MaterialV1).language === 'sv' && (b as MaterialV1).language !== 'sv') {
    return -1;
  }
  return 0;
};

watch(
  () => result.value,
  () => {
    const currentSortKey = searchPanelRef.value?.selectedSortOption?.key;
    const shouldSortGlobally = currentSortKey === 'titleAsc' || currentSortKey === 'titleDesc';

    const input = shouldSortGlobally ? [...result.value].sort(sortBySwedishFirst) : result.value;
    deduplicatedMaterials.value = groupContentByOriginalTitle(input, sortBySwedishFirst) as MaterialV1[];
  },
);

const toggleFilter = () => {
  if (!isFiltersVisible.value) {
    rememberFocus();
  }
  isFiltersVisible.value = !isFiltersVisible.value;
};

onMounted(() => {
  if (subscriptions.value.length) {
    loadCategories(subscriptions.value[0].type);
  }
});

const selectedCategory = computed<string>(
  () => selectedFilters.value['categories']?.[0]?.value ?? MATERIAL_CATEGORY_ALL_ID,
);

const onCategoryChange = (id: string) => {
  if (id === MATERIAL_CATEGORY_ALL_ID) {
    filtersToQuery({ categories: [] });
    return;
  }
  const category = materialCategoryItems.value.find((c) => c.id === id);
  filtersToQuery({ categories: [{ value: id, label: category?.title ?? id, count: 1 }] });
};

const handleBack = () => {
  router.push({ name: 'Materials' });
};
</script>

<template>
  <ContentWithFiltersLayout
    :is-filters-visible="isFiltersVisible"
    @remove-all-filters="removeAllFilters"
    @toggle-filter="toggleFilter"
  >
    <template #title>
      <h1 class="text-secondary mb-0">{{ t(l.material.title) }}</h1>
    </template>
    <template #top-panel>
      <SearchPanel
        ref="searchPanelRef"
        :sort-fields="sortFields"
        selected-sort-option="lastAdded"
        :filters="filters as Filter"
        :filter-fields="filterFields"
        :limit="limit"
        content-type="Material"
        :focused="false"
        :estimated-nr-grid-cells="100"
        :search-subscription="subscriptions.map((subscription) => subscription.type)"
        :back-button-label="t(l.button_all_categories)"
        @toggleFilter="toggleFilter"
        @back="handleBack"
      ></SearchPanel>
    </template>
    <template #content>
      <v-select
        :model-value="selectedCategory"
        :aria-label="t(l.label_category)"
        class="category-select mb-2"
        density="comfortable"
        :items="materialCategoryItems"
        item-value="id"
        item-title="title"
        :list-props="{ bgColor: 'bg-surface-bright' }"
        variant="plain"
        hide-details
        @update:model-value="onCategoryChange"
      >
        <template #selection="{ item }">
          <v-icon :icon="`fa:${item.raw.selectIcon}`" size="small" class="mr-2" />
          {{ item.title }}
        </template>
        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps">
            <template #prepend>
              <v-icon :icon="`fa:${item.raw.selectIcon}`" size="small" class="mr-2" />
            </template>
          </v-list-item>
        </template>
      </v-select>
      <MaterialResults
        :is-loading="isLoading"
        :deduplicated-materials="deduplicatedMaterials"
        :has-more="hasMore"
        source="materials_search"
        @load-more="next"
      ></MaterialResults>
    </template>
    <template #filters="{ filterTop }">
      <SearchFilters
        content-type="Material"
        :filter-fields="filterFields"
        :current-subscription="subscriptions[0].type"
        :filter-top="filterTop"
      />
    </template>
  </ContentWithFiltersLayout>
</template>

<style scoped lang="scss">
.category-select {
  width: fit-content;
  caret-color: transparent;

  :deep(.v-field) {
    --v-theme-surface-container-lowest: var(--v-theme-surface-bright);
  }

  :deep(.v-field:hover),
  :deep(.v-field--focused) {
    background-color: rgb(var(--v-theme-on-surface-variant)) !important;
    border-radius: 10px;
  }

  :deep(.v-field__input) {
    font-weight: 600;
    opacity: 1;
    padding-left: 10px;
  }
}
</style>
